import { IpaType } from "../phonemes/ipa";
import { PhoneLengths } from "../rules/lengthRules";
import { convertSynthToIpa } from "./convertSynthToIpa";
import { mediumBreak, shortBreak } from "./exact";
import { ipaToSynth } from "./ipaToSynth";
import { meSpeakOptions } from "./meSpeak";
import { playAudio } from "./playAudio";

/**
 * leverages meSpeak to speak phonemes aloud
 * @param input IPA phonemes, e.g. 'ðɪs ɪz ə tɛst'
 * @param overrides [optional] if you wish to override the default playback settings
 * @param addPhonemeLength [optional] function that adds length modifiers based on phoneme
 * @param onDone [optional] function called when audio is done
 * @returns a list of unsupported speak phonemes, or 'undefined' if none
 */
export function speakPhonemes(
  input: string, overrides?: meSpeakOptions, addLengths?: PhoneLengths, onDone?: () => void
): { asSynth: string, approxIpa: IpaType, unsupported: string[] | undefined } {
  // sound doesn't work on the iPhone till audio is played, so this can unstick the system
  playAudio('null.wav');

  const meSpeakInput = ipaToSynth(input, addLengths);
  const approxIpa = checkIpaConversion(meSpeakInput.speak, true);

  console.log(meSpeakInput.speak);
  console.log('/' + approxIpa + '/');

  const options = { ...speakDefaults, ...overrides };
  if (options.immediate === true) {
    meSpeak.speak('[[' + meSpeakInput.speak + ']]', options);
  } else {
    espeakManager.speak(meSpeakInput.speak, options, onDone);
  }

  return { asSynth: meSpeakInput.speak, approxIpa, unsupported: meSpeakInput.unsupported };
}

/** speak using eSpeak's phoneme encodings */
export function speakEspeak(input: string, overrides?: meSpeakOptions, onDone?: () => void) {
  const options = { ...speakDefaults, ...overrides };
  espeakManager.speak(input, options, onDone);
}

export function stopEspeak() {
  meSpeak.stop();
}

/** last audio clip played */
export let lastAudio: ArrayBuffer | null = null;
/** copy each audio clip as it's played */
export function setCopyLastAudio(doCopy: boolean) { copyLastAudio = doCopy; }
export let copyLastAudio = false;

export let speakDefaults: meSpeakOptions =
  { amplitude: 100, wordgap: 0, pitch: 50, speed: 170, variant: "fiona", immediate: false };

function checkIpaConversion(speak: string, asMs?: boolean): string {
  const backAgain = convertSynthToIpa(speak, asMs);
  const ipa = backAgain[0].join('');
  if (backAgain[1].length > 0) {
    console.log('error converting espeak to ipa', backAgain[1]);
  }
  return ipa;
}


/**
 * Espeak has problems with large amounts of phonemes, eventually
 * just babbling after too many. So instead we'll feed it individual
 * sentences. Also, since espeak->audio can take a non-trivial amount
 * of time, we start encoding the next sentence as we play the current
 * one. Also useful for starting sound earlier, since we don't need
 * to encode everything all at once. And it breaks up really long words,
 * since espeak also has trouble with those.
 * NOTE: this assumes reasonably sized sentences, or at least
 * reasonably sized clauses separated by pauses.
 */
class EspeakManager {
  private sentences: string[] = [];
  private options: meSpeakOptions = {};
  private queue: meSpeak.AudioData | null = null;
  private playing = false;
  private onDone?: () => void;

  /** kick off speaking, possibly breaking it into pieces behind the scenes */
  speak(espeak: string, options: meSpeakOptions, onDone?: () => void): void {
    meSpeak.stop();
    this.sentences = makeStack(espeak);
    this.options = { ...options, rawdata: true };
    this.onDone = onDone;
    this.startNext();
  }

  private startNext(): void {
    const next = this.sentences.pop();
    if (!next) {
      this.sentences = [];
      return;
    }

    // start encoding next sentence in the background
    const synth = this.toSynth(next);
    meSpeak.speak(synth, this.options,
      (s: boolean, i: number, a: meSpeak.AudioData) => this.afterEncode(s, i, a));
  }

  private toSynth(s: string): string {
    // pull ?! outside the [[]], adding a comma for an extra pause
    if (s.endsWith('_?')) {
      return `[[${s.substring(0, s.length - 2)}]] ? ,`;
    } else if (s.endsWith('_!')) {
      return `[[${s.substring(0, s.length - 2)}]] ! ,`;
    }
    return '[[' + s + ']]';
  }

  private afterEncode(success: boolean, id: number, audio: meSpeak.AudioData): void {
    if (!success) {
      this.sentences = [];
      return;
    }
    this.queue = audio;
    if (copyLastAudio) {
      lastAudio = (audio as ArrayBuffer).slice(0);
    }
    if (!this.playing) {
      this.playQueueAndStartNext();
    }
  }

  private afterPlay(success: boolean): void {
    this.playing = false;
    if (!success) {
      this.sentences = [];
    } else if (this.queue) {
      this.playQueueAndStartNext();
    } else if (this.sentences.length === 0) {
      if (this.onDone) this.onDone();
    }
  }

  private playQueueAndStartNext(): void {
    this.playing = true;
    meSpeak.play(this.queue, undefined, (s: boolean) => this.afterPlay(s), undefined, this.options.pan);
    this.queue = null;
    this.startNext();
  }
}
const espeakManager = new EspeakManager();

/**
 * splits up text into smaller pieces (like sentences) to avoid espeak limitations
 * @returns backwards list so it can be treated as a stack
 */
export // for tests
  function makeStack(s: string): string[] {
  // split on . or ? or !, which are _. and _? and _!
  const stack: string[] = [];
  let start = 0;
  while (true) {
    const period = s.indexOf('_.', start);
    const question = s.indexOf('_?', start);
    const exclaim = s.indexOf('_!', start);
    if (period === -1 && question === -1 && exclaim === -1) {
      pushSentence(stack, s.substring(start));
      break;
    }

    const end = getFirst(period, question, exclaim) + 2;
    pushSentence(stack, s.substring(start, end));
    start = end;
  }
  return stack.reverse();
}

function getFirst(period: number, question: number, exclaim: number): number {
  return Math.min(
    period === -1 ? Number.MAX_SAFE_INTEGER : period,
    question === -1 ? Number.MAX_SAFE_INTEGER : question,
    exclaim === -1 ? Number.MAX_SAFE_INTEGER : exclaim,
  );
}

/** if there's content, add it to the stack, breaking up long words if needed */
function pushSentence(stack: string[], sentence: string) {
  sentence = sentence.trim();
  if (sentence.length === 0) {
    return;
  }

  // if sentence is too long, break on semi-colon;
  // note that this changes the prosody, sounding like the end of a sentence rather than a phrase,
  // but it's better than eSpeak turning the ending into gibberish
  if (sentence.length > maxPhraseLength) {
    const semiColon = sentence.lastIndexOf(mediumBreak, maxPhraseLength);
    if (semiColon > 0) {
      pushSentence(stack, sentence.substring(0, semiColon + 2));
      pushSentence(stack, sentence.substring(semiColon + 3));
      return;
    }
    // no ; so try ,
    const comma = sentence.lastIndexOf(shortBreak, maxPhraseLength);
    if (comma > 0) {
      pushSentence(stack, sentence.substring(0, comma + 2));
      pushSentence(stack, sentence.substring(comma + 3));
      return;
    }
  }

  // if any word is too long, break it apart
  const inWords = sentence.split(' ');
  const outWords = inWords.flatMap(word => word.length < maxWordLength ? word : breakWord(word));
  stack.push(outWords.join(' '));
}

/** break word into pieces below max that should still sound reasonable when converted to sound */
function breakWord(word: string): string[] {
  const words: string[] = [];
  const len = word.length;
  for (let start = 0; start < len;) {
    const end = findPrevStress(word, start, Math.min(start + maxWordLength, len));
    words.push(word.substring(start, end));
    start = start === end ? start + 1 : end;  // be extra cautious about an infinite loop
  }
  return words;
}

function findPrevStress(word: string, start: number, end: number): number {
  if (end - start < maxWordLength) {
    // remaining string is short enough to use as-is
    return end;
  }
  // search backwards from max to find last primary stress
  for (let i = end - 1; i > start + 1; --i) {
    if (word[i] === "'") {
      return i;
    }
  }
  // no stress marker, so chop it at max size
  return start + maxWordLength;
}

/** espeak generates garbage if passed a long phrase, this is just under the limit */
const maxPhraseLength = 680;
/** espeak generates garbage if passed long words, this is just under the limit */
const maxWordLength = 170;
